import Editor from '@monaco-editor/react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { forwardRef, useEffect, useRef } from 'react';

class JsonataMonacoManager {
    monaco: any;
    isLanguageRegistered: boolean;
    disposer: any;
    jsonataBuiltins: any;

    constructor() {
        this.monaco = null;
        this.isLanguageRegistered = false;
        this.disposer = null;
        this.jsonataBuiltins = this.getJsonataBuiltins();
    }

    setMonaco(monaco: any) {
        if (this.monaco) {
            return;
        }
        this.monaco = monaco;
    }

    registerLanguage() {
        if (!this.monaco || this.isLanguageRegistered) {
            return;
        }
        const monaco = this.monaco;
        monaco.languages.register({ id: 'jsonata' });
        monaco.languages.setMonarchTokensProvider('jsonata', {
            tokenizer: {
                root: [
                    [/\$[a-zA-Z_]\w*/, 'keyword'],
                    [/[a-zA-Z_]\w*/, 'identifier'],
                    [/\d+/, 'number'],
                    [/["]([^"\\]|\\.)*["]|[\']([^\'\\]|\\.)*[\']/, 'string'],
                    [/[{\[\]()]/, '@brackets'],
                    [/[:=]/, 'operator'],
                    [/[\.,]/, 'delimiter'],
                ],
            },
        });
        monaco.languages.setLanguageConfiguration('jsonata', {
            brackets: [
                ['{', '}'],
                ['[', ']'],
                ['(', ')'],
            ],
            autoClosingPairs: [
                { open: '{', close: '}' },
                { open: '[', close: ']' },
                { open: '(', close: ')' },
                { open: '"', close: '"', notIn: ['string'] },
                { open: "'", close: "'", notIn: ['string'] },
            ],
            surroundingPairs: [
                { open: '{', close: '}' },
                { open: '[', close: ']' },
                { open: '(', close: ')' },
                { open: '"', close: '"' },
                { open: "'", close: "'" },
            ],
        });
        this.isLanguageRegistered = true;
    }

    getJsonataBuiltins() {
        const monaco = this.monaco;
        const kind = monaco ? monaco.languages.CompletionItemKind.Function : 3;
        const f = (name: string, signature: string, description: string) => ({ name, signature, description, kind });
        return [
            // String Functions
            f('$string', '$string(arg, prettify)', 'Casts the argument to a string'),
            f('$length', '$length(str)', 'Returns the number of characters in a string'),
            f('$substring', '$substring(str, start, length)', 'Returns a substring from a start position'),
            f(
                '$substringBefore',
                '$substringBefore(str, chars)',
                'Returns the substring before the first occurrence of a character set',
            ),
            f(
                '$substringAfter',
                '$substringAfter(str, chars)',
                'Returns the substring after the first occurrence of a character set',
            ),
            f('$uppercase', '$uppercase(str)', 'Converts a string to uppercase'),
            f('$lowercase', '$lowercase(str)', 'Converts a string to lowercase'),
            f('$trim', '$trim(str)', 'Normalizes and trims whitespace from a string'),
            f('$pad', '$pad(str, width, char)', 'Pads a string to a specified width'),
            f('$contains', '$contains(str, pattern)', 'Returns true if a string contains a pattern'),
            f('$split', '$split(str, separator, limit)', 'Splits a string into an array of substrings'),
            f('$join', '$join(array, separator)', 'Joins an array of strings with a separator'),
            f('$match', '$match(str, pattern, limit)', 'Applies a regex pattern to a string and returns all matches'),
            f(
                '$replace',
                '$replace(str, pattern, replacement, limit)',
                'Replaces occurrences of a pattern with a replacement',
            ),
            f('$eval', '$eval(expr, context)', 'Parses and evaluates a JSONata expression'),
            f('$base64encode', '$base64encode(str)', 'Encodes a string to Base64'),
            f('$base64decode', '$base64decode(str)', 'Decodes a Base64 string'),
            f('$encodeUrlComponent', '$encodeUrlComponent(str)', 'URL-encodes a string component'),
            f('$encodeUrl', '$encodeUrl(str)', 'URL-encodes a full URL'),
            f('$decodeUrlComponent', '$decodeUrlComponent(str)', 'URL-decodes a string component'),
            f('$decodeUrl', '$decodeUrl(str)', 'URL-decodes a full URL'),

            // Numeric Functions
            f('$number', '$number(arg)', 'Casts the argument to a number'),
            f('$abs', '$abs(number)', 'Returns the absolute value of a number'),
            f('$floor', '$floor(number)', 'Returns the largest integer less than or equal to a number'),
            f('$ceil', '$ceil(number)', 'Returns the smallest integer greater than or equal to a number'),
            f(
                '$round',
                '$round(number, precision)',
                'Returns a number rounded to a specified number of decimal places',
            ),
            f('$power', '$power(base, exponent)', 'Returns a base number raised to the power of an exponent'),
            f('$sqrt', '$sqrt(number)', 'Returns the square root of a number'),
            f('$random', '$random()', 'Returns a pseudo-random number between 0 and 1'),
            f(
                '$formatNumber',
                '$formatNumber(number, picture, options)',
                'Formats a number according to a picture string',
            ),
            f('$formatBase', '$formatBase(number, radix)', 'Formats a number in a specified radix'),

            // Array Functions
            f('$count', '$count(array)', 'Returns the number of items in an array'),
            f('$append', '$append(array1, array2)', 'Appends the second array to the first'),
            f('$sort', '$sort(array, function)', 'Sorts an array, optionally using a comparator function'),
            f('$reverse', '$reverse(array)', 'Returns an array in reverse order'),
            f('$shuffle', '$shuffle(array)', 'Returns an array shuffled in random order'),
            f('$distinct', '$distinct(array)', 'Returns an array with duplicates removed'),
            f('$zip', '$zip(array1, array2, ...)', 'Returns a convolved array of grouped values'),

            // Object Functions
            f('$keys', '$keys(object)', 'Returns an array of object keys'),
            f('$lookup', '$lookup(object, key)', 'Returns the value associated with a key in an object'),
            f('$spread', '$spread(object)', 'Splits an object into an array of key-value pairs'),
            f('$merge', '$merge(array)', 'Merges an array of objects into a single object'),
            f('$sift', '$sift(object, function)', 'Returns an object with entries that satisfy a predicate function'),
            f('$each', '$each(object, function)', 'Applies a function to each key-value pair of an object'),
            f('$error', '$error(message)', 'Throws an error with an optional message'),
            f('$assert', '$assert(condition, message)', 'Throws an error if a condition is false'),
            f('$type', '$type(value)', 'Returns the type of a value as a string'),

            // Aggregation Functions
            f('$sum', '$sum(array)', 'Returns the sum of a numeric array'),
            f('$max', '$max(array)', 'Returns the maximum value in an array'),
            f('$min', '$min(array)', 'Returns the minimum value in an array'),
            f('$average', '$average(array)', 'Returns the average of a numeric array'),

            // Boolean Functions
            f('$boolean', '$boolean(arg)', 'Casts an argument to a boolean'),
            f('$not', '$not(arg)', 'Returns the logical NOT of an argument'),
            f('$exists', '$exists(arg)', 'Returns true if an argument exists'),

            // Date/Time Functions
            f('$now', '$now()', 'Returns the current timestamp'),
            f('$millis', '$millis()', 'Returns the current time in milliseconds since the epoch'),
            f('$fromMillis', '$fromMillis(number)', 'Converts milliseconds to an ISO 8601 timestamp'),
            f('$toMillis', '$toMillis(timestamp)', 'Converts an ISO 8601 timestamp to milliseconds'),

            // Higher Order Functions
            f('$map', '$map(array, function)', 'Applies a function to each item in an array'),
            f('$filter', '$filter(array, function)', 'Returns array items that satisfy a predicate function'),
            f('$single', '$single(array, function)', 'Returns a single item that satisfies a predicate'),
            f('$reduce', '$reduce(array, function, init)', 'Applies a reduction function to an array'),
        ];
    }

    isInsideJsonataExpression(text: string) {
        const openCount = (text.match(/{%/g) || []).length;
        const closeCount = (text.match(/%}/g) || []).length;
        return openCount > closeCount;
    }

    parseContext(model: any, position: any) {
        const line = model.getLineContent(position.lineNumber);
        const textBefore = line.substring(0, position.column - 1);
        const insideJsonata = this.isInsideJsonataExpression(
            model.getValueInRange({
                startLineNumber: 1,
                startColumn: 1,
                endLineNumber: position.lineNumber,
                endColumn: position.column,
            }),
        );
        const word = model.getWordUntilPosition(position);
        const isAfterDot = /\.\s*\w*$/.test(textBefore);
        const variableWithDotMatch = textBefore.match(/(\$[\w.\[\]\$]+)\.\s*(\w*)$/);
        const isAfterVariableWithDot = !!variableWithDotMatch;
        const fullPathBeforeDot = variableWithDotMatch ? variableWithDotMatch[1] : null;
        const partialPropertyText = variableWithDotMatch ? variableWithDotMatch[2] : '';
        const dollarMatch = !isAfterDot ? textBefore.match(/\$(\w*)$/) : null;
        const isAfterDollar = !!dollarMatch;
        const partialDollarText = dollarMatch ? dollarMatch[1] : '';
        const isAfterOpenBracket = /\[\s*\w*$/.test(textBefore);
        const isAfterOpenBrace = /\{\s*$/.test(textBefore);
        return {
            insideJsonata,
            isAfterDot,
            isAfterDollar,
            isAfterVariableWithDot,
            isAfterOpenBracket,
            isAfterOpenBrace,
            partialDollarText,
            partialPropertyText,
            fullPathBeforeDot,
            word,
            textBefore,
        };
    }

    calculateReplacementRange(word: any, position: any, ctx: any) {
        const range = {
            startLineNumber: position.lineNumber,
            endLineNumber: position.lineNumber,
            startColumn: word.startColumn,
            endColumn: word.endColumn,
        };
        if (ctx.isAfterDollar) {
            const i = ctx.textBefore.lastIndexOf('$');
            if (i !== -1) {
                range.startColumn = i + 1;
            }
        } else if (ctx.isAfterVariableWithDot || ctx.isAfterDot) {
            const i = ctx.textBefore.lastIndexOf('.');
            if (i !== -1) {
                range.startColumn = i + 2;
            }
        }
        return range;
    }

    registerCompletionProvider(schemaSuggestions = []) {
        if (!this.monaco) {
            throw new Error('Monaco not initialized');
        }
        if (this.disposer) {
            try {
                this.disposer.dispose();
            } catch (e) {}
            this.disposer = null;
        }
        const monaco = this.monaco;
        this.disposer = monaco.languages.registerCompletionItemProvider('jsonata', {
            triggerCharacters: ['.', '[', ']', '$', '(', '{'],
            provideCompletionItems: (model: any, position: any) => {
                const ctx = this.parseContext(model, position);
                const range = this.calculateReplacementRange(ctx.word, position, ctx);
                let suggestions: any[] = [];

                if (!ctx.insideJsonata) {
                    suggestions.push({
                        label: 'JSONata expression',
                        kind: monaco.languages.CompletionItemKind.Snippet,
                        insertText: ctx.isAfterOpenBrace ? '% ${1:expression} %}' : '{% ${1:expression} %}',
                        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                        documentation: 'Insert JSONata expression wrapper',
                        range,
                    });
                    return { suggestions };
                }

                if (ctx.isAfterVariableWithDot) {
                    const normalizedPath = ctx.fullPathBeforeDot.replace(/\[[^\]]+\]/g, '[0]');
                    const expectedPrefix = normalizedPath + '.';
                    const propMatches = schemaSuggestions
                        .filter((s: any) => s.insertText?.toLowerCase().startsWith(expectedPrefix.toLowerCase()))
                        .map((s: any) => ({ ...s, insertText: s.insertText.substring(expectedPrefix.length) }))
                        .filter((s: any) => !s.insertText.includes('.'))
                        .filter((s: any) =>
                            s.insertText.toLowerCase().startsWith(ctx.partialPropertyText.toLowerCase()),
                        )
                        .map((s: any) => ({ ...s, range }));
                    suggestions = suggestions.concat(propMatches);
                } else if (ctx.isAfterDollar) {
                    const p = ctx.partialDollarText.toLowerCase();
                    suggestions = suggestions.concat(
                        this.jsonataBuiltins
                            .filter((f: any) => f.name.toLowerCase().startsWith('$' + p))
                            .map((f: any) => ({
                                label: f.name,
                                kind: f.kind,
                                insertText: f.name,
                                detail: f.signature,
                                documentation: f.description,
                                range,
                            })),
                    );
                    const roots = new Set();
                    schemaSuggestions.forEach((s: any) => {
                        const m = s.insertText?.match(/^(\$\w+)/);
                        if (m && m[1].toLowerCase().startsWith('$' + p) && !roots.has(m[1])) {
                            roots.add(m[1]);
                            suggestions.push({
                                label: m[1],
                                kind: monaco.languages.CompletionItemKind.Variable,
                                insertText: m[1],
                                documentation: s.documentation || `Variable: ${m[1]}`,
                                range,
                            });
                        }
                    });
                } else if (ctx.isAfterOpenBracket) {
                    suggestions.push(
                        {
                            label: '0',
                            kind: monaco.languages.CompletionItemKind.Value,
                            insertText: '0',
                            documentation: 'First array element',
                            range,
                        },
                        {
                            label: '-1',
                            kind: monaco.languages.CompletionItemKind.Value,
                            insertText: '-1',
                            documentation: 'Last array element',
                            range,
                        },
                        {
                            label: '?condition',
                            kind: monaco.languages.CompletionItemKind.Snippet,
                            insertText: '?${1:condition}',
                            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                            documentation: 'Filter condition',
                            range,
                        },
                    );
                } else {
                    suggestions = suggestions.concat(
                        this.jsonataBuiltins.map((f: any) => ({
                            label: f.name,
                            kind: f.kind,
                            insertText: f.name,
                            detail: f.signature,
                            documentation: f.description,
                            range,
                        })),
                    );
                    suggestions = suggestions.concat(schemaSuggestions.map((s: any) => ({ ...s, range })));
                }

                return { suggestions };
            },
        });
        return this.disposer;
    }
}

function toSuggestionsFromPaths(paths: any[] = [], monaco: any) {
    const kind = monaco ? monaco.languages.CompletionItemKind.Field : 5;
    return paths.map((insertText: string) => ({ insertText, label: insertText.replace(/^\$?/, ''), kind }));
}

const MonacoInput = forwardRef((props: any, ref: any) => {
    const {
        value,
        onChange,
        monacoHeight,
        paletteMode,
        lineNumbers,
        minimap,
        disabled,
        readonly,
        options,
        jsonataPaths,
        jsonSchema,
        managerRef,
        disposerRef,
    } = props;

    return (
        <Box
            ref={ref}
            sx={{
                height: monacoHeight,
                width: '100%',
                '& .monaco-editor': {
                    paddingTop: 0,
                    paddingBottom: 0,
                },
                '& .monaco-editor .overflow-guard': {
                    borderRadius: 0,
                },
            }}
        >
            <Editor
                height={monacoHeight}
                language={'jsonata'}
                defaultValue={''}
                value={value || ''}
                onChange={onChange}
                options={{
                    automaticLayout: true,
                    theme: paletteMode === 'light' ? 'vs-light' : 'vs-dark',
                    lineNumbers: lineNumbers,
                    glyphMargin: false,
                    folding: false,
                    renderLineHighlight: 'none',
                    scrollBeyondLastLine: false,
                    wordWrap: 'on',
                    wrappingIndent: 'same',
                    fontFamily: 'inherit',
                    fontSize: 16,
                    lineHeight: 24,
                    overviewRulerLanes: 0,
                    overviewRulerBorder: false,
                    lineDecorationsWidth: 0,
                    lineNumbersMinChars: 0,
                    padding: { top: 0, bottom: 0 },
                    minimap: { enabled: minimap },
                    scrollbar: {
                        vertical: 'auto',
                        horizontal: 'hidden',
                        useShadows: false,
                        verticalScrollbarSize: 10,
                    },
                    readOnly: disabled || readonly,
                    ...options,
                }}
                onMount={(editor, monaco) => {
                    const mgr = managerRef.current;
                    mgr.setMonaco(monaco);
                    mgr.registerLanguage();

                    let suggestions: any[] = [];
                    const pathsArray: any[] = Array.isArray(jsonataPaths) ? jsonataPaths : [];
                    if (pathsArray.length > 0) {
                        suggestions = toSuggestionsFromPaths(pathsArray, monaco);
                    } else if (jsonSchema) {
                        const fromSchema: any[] = [];
                        const walk = (node: any, prefix: string) => {
                            if (!node || typeof node !== 'object') {
                                return;
                            }
                            if (node.type === 'object' && node.properties && typeof node.properties === 'object') {
                                Object.keys(node.properties).forEach((key) => {
                                    const child = node.properties[key];
                                    const next = prefix ? `${prefix}.${key}` : key;
                                    fromSchema.push(next);
                                    walk(child, next);
                                });
                            }
                            if (node.type === 'array' && node.items) {
                                const next = `${prefix}[0]`;
                                fromSchema.push(next);
                                walk(node.items, next);
                            }
                        };
                        walk(jsonSchema, '');
                        suggestions = toSuggestionsFromPaths(
                            fromSchema.map((p) => (p.startsWith('$') ? p : `$${p}`)),
                            monaco,
                        );
                    }

                    if (disposerRef.current) {
                        try {
                            disposerRef.current.dispose();
                        } catch (e) {}
                    }
                    disposerRef.current = mgr.registerCompletionProvider(suggestions);
                }}
            />
        </Box>
    );
});

MonacoInput.displayName = 'MonacoInput';

const JsonataEditorWidget = (props: any) => {
    const { id, label, value, onChange, theme, required, disabled, readonly, placeholder, rawErrors = [] } = props;
    const config = props && props.options && typeof props.options === 'object' ? props.options : props.schema;
    const { minimap = false, lineNumbers = 'off', rows, options, jsonataPaths, jsonSchema } = config;
    const paletteMode = theme.palette.mode;

    const managerRef = useRef(new JsonataMonacoManager());
    const disposerRef = useRef<any>(null);

    const handleEditorChange = (newValue: any) => {
        onChange(newValue);
    };

    useEffect(() => {
        return () => {
            if (disposerRef.current) {
                try {
                    disposerRef.current.dispose();
                } catch (e) {}
                disposerRef.current = null;
            }
        };
    }, []);

    const displayRows = typeof rows === 'string' || typeof rows === 'number' ? rows : 5;
    const monacoHeight = Number(displayRows) * 24;

    return (
        <TextField
            id={id}
            label={label}
            placeholder={placeholder}
            required={required}
            disabled={disabled || readonly}
            error={rawErrors.length > 0}
            fullWidth
            multiline
            rows={displayRows}
            value={value || ''}
            onChange={() => {}}
            InputProps={{
                inputComponent: MonacoInput,
                inputProps: {
                    value,
                    onChange: handleEditorChange,
                    monacoHeight,
                    paletteMode,
                    lineNumbers,
                    minimap,
                    disabled,
                    readonly,
                    options,
                    jsonataPaths,
                    jsonSchema,
                    managerRef,
                    disposerRef,
                },
            }}
        />
    );
};

export default JsonataEditorWidget;
